<script setup lang="ts">
import { computed } from "vue"
import { useI18n } from "vue-i18n"

import Icon from "@/components/shared/Icon.vue"
import { localeLabels, setLocale, supportedLocales } from "@/locales/index"
import router from "@/plugins/router"
import store from "@/plugins/store"
import type { Partner } from "@/types/partner.type"

const props = defineProps({
  title: String,
})

const emits = defineEmits(["toggleSideMenu"])

function toggleSideMenu() {
  emits("toggleSideMenu")
}

const partner = computed<Partner | null>(() => store.getters.partner)

const localeOptions = supportedLocales.map((code) => ({ code, label: localeLabels[code] || code }))
const { locale } = useI18n()
const currentLocale = computed(() => locale.value)

function pickLocaleOption(val: string) {
  setLocale(val)
}

function logOut() {
  store.commit("clearAuth")
  router.replace("/log_in")
}
</script>

<template>
  <header>
    <div class="container-fluid">
      <div class="border-box d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center title">
          <Icon icon="bars" duotone class="d-lg-none" @click="toggleSideMenu" />
          <h2>{{ title || partner?.name }}</h2>
        </div>
        <div class="d-flex align-items-center">
          <div class="dropdown">
            <button
              class="btn btn-sm btn-link dropdown-toggle text-black"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {{ localeLabels[currentLocale] }}
            </button>
            <ul class="dropdown-menu">
              <li v-for="option in localeOptions" :key="option.code">
                <button class="dropdown-item" @click="pickLocaleOption(option.code)">{{ option.label }}</button>
              </li>
            </ul>
          </div>
          <div class="dropdown ms-3">
            <button
              class="btn btn-sm btn-outline-secondary dropdown-toggle"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <Icon icon="user" duotone />
            </button>
            <ul class="dropdown-menu">
              <li>
                <button class="dropdown-item" @click="logOut()">{{ $t("nav.logOut") }}</button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
header {
  height: var(--header-height);
  z-index: 999;
}

.container-fluid {
  height: 100%;
  padding-left: var(--container-padding);
  padding-right: var(--container-padding);
}

.border-box {
  height: 100%;
  border-bottom: 1px solid #e0e0de;
}

.title i {
  font-size: 24px;
  margin-right: 0.75rem;
  cursor: pointer;
}

.title h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}
</style>
